<script>
  import { onDestroy, onMount } from 'svelte';
  import { AlertCircle, LoaderCircle, Search, UserRound, X } from 'lucide-svelte';
  import {
    activateConversation,
    conversations,
    createDirectConversationWS,
    searchUsersWS
  } from '$lib/websocket-client.js';
  import { avatarContentUrl } from '$lib/avatar';

  export let onClose = () => {};
  export let onCreated = () => {};
  export let showTitle = true;

  let keyword = '';
  let users = [];
  let loading = false;
  let creatingUserId = null;
  let error = '';
  let debounceTimer;
  let searchSequence = 0;
  let lastQueryKey = '';
  let hiddenExistingCount = 0;

  $: existingConversationUserIds = new Set(
    ($conversations || []).filter((item) => item?.type === 'direct' && item?.userId).map((item) => String(item.userId))
  );

  $: {
    const queryKey = keyword.trim();

    if (queryKey !== lastQueryKey) {
      lastQueryKey = queryKey;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => runSearch(), 320);
    }
  }

  onMount(() => {
    lastQueryKey = keyword.trim();
    runSearch();
  });

  onDestroy(() => {
    clearTimeout(debounceTimer);
    searchSequence += 1;
  });

  function getInitial(name = '') {
    return (
      String(name || '?')
        .trim()
        .charAt(0)
        .toUpperCase() || '?'
    );
  }

  async function runSearch() {
    const searchKeyword = keyword.trim();
    const seq = ++searchSequence;
    loading = true;
    error = '';

    try {
      const rows = await searchUsersWS({
        keyword: searchKeyword,
        scope: 'system',
        limit: 36,
        excludeExisting: true
      });

      if (seq !== searchSequence) return;
      const nextUsers = rows.filter((user) => !existingConversationUserIds.has(String(user?.id)));
      hiddenExistingCount = rows.length - nextUsers.length;
      users = nextUsers;
    } catch (err) {
      if (seq !== searchSequence) return;
      if (!users.length) {
        hiddenExistingCount = 0;
      }
      error = err?.message || 'Không thể tìm kiếm người dùng.';
    } finally {
      if (seq === searchSequence) {
        loading = false;
      }
    }
  }

  async function startConversation(user) {
    if (!user?.id || creatingUserId) return;

    try {
      creatingUserId = user.id;
      error = '';

      const conversation = await createDirectConversationWS(user.id);
      await activateConversation(conversation);

      onCreated?.(conversation);
      onClose?.();
    } catch (err) {
      error = err?.message || 'Không thể thêm bạn.';
    } finally {
      creatingUserId = null;
    }
  }
</script>

<section
  class="flex min-h-0 max-h-[88dvh] w-full flex-col overflow-hidden rounded-2xl bg-white text-slate-900 shadow-xl sm:max-h-[720px]">
  {#if showTitle}
    <header class="flex items-center justify-between gap-4 border-b border-slate-200 px-5 py-4 sm:px-6">
      <h3 class="text-xl font-semibold text-slate-950 sm:text-2xl">Thêm bạn (^.^)</h3>

      <button
        type="button"
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-900 transition hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-100"
        aria-label="Đóng"
        onclick={onClose}>
        <X size={24} strokeWidth={2} />
      </button>
    </header>
  {/if}

  <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
    <div class="space-y-4 border-b border-slate-200 px-5 py-4 sm:px-6">
      <div class="relative">
        <Search size={18} class="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          bind:value={keyword}
          type="search"
          autocomplete="off"
          placeholder="Nhập tên, email hoặc số điện thoại"
          class="h-12 w-full rounded-full border border-slate-200 bg-white pl-11 pr-4 text-sm text-slate-950 shadow-sm outline-none placeholder:text-sm placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 sm:text-base" />
      </div>

      <div class="flex items-center justify-between gap-3 text-xs text-slate-500 sm:text-sm">
        <span>Kết quả toàn hệ thống, đã lọc người có trong conversation.</span>
        {#if hiddenExistingCount > 0}
          <span class="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
            Ẩn {hiddenExistingCount} trùng
          </span>
        {/if}
      </div>
    </div>

    {#if error}
      <div
        class="mx-6 mt-4 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
        <AlertCircle size={16} class="mt-0.5 shrink-0" />
        <span>{error}</span>
      </div>
    {/if}

    <div class="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
      <div class="mb-3 flex items-center justify-between gap-3">
        <h4 class="text-base font-semibold text-slate-950 sm:text-lg">User toàn hệ thống</h4>
        {#if loading}
          <span class="inline-flex items-center gap-2 text-xs text-slate-500 sm:text-sm">
            <LoaderCircle size={15} class="animate-spin text-blue-600" />
            Đang tải
          </span>
        {/if}
      </div>

      {#if users.length}
        <div class="space-y-1 min-h-[12rem]">
          {#each users as user (user.id)}
            <button
              type="button"
              class="flex w-full min-w-0 items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"
              disabled={!!creatingUserId}
              onclick={() => startConversation(user)}>
              <span
                class="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-sm font-semibold text-slate-600">
                {#if avatarContentUrl(user.avatar)}
                  <img src={avatarContentUrl(user.avatar)} alt={user.name} class="h-full w-full object-cover" loading="lazy" decoding="async" />
                {:else}
                  {getInitial(user.name)}
                {/if}

                {#if user.online}
                  <span class="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500">
                  </span>
                {/if}
              </span>

              <span class="min-w-0 flex-1">
                <span class="block truncate text-sm font-medium text-slate-950 sm:text-base">{user.name}</span>
                <span class="block truncate text-xs text-slate-500 sm:text-sm">
                  {user.email || user.phone || 'Người dùng trong hệ thống'}
                </span>
              </span>

              <span class="shrink-0 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                {creatingUserId === user.id ? 'Đang thêm...' : 'Kết bạn'}
              </span>
            </button>
          {/each}
        </div>
      {:else if loading}
        <div class="flex h-44 flex-col items-center justify-center gap-3 text-sm text-slate-500">
          <LoaderCircle size={20} class="animate-spin text-blue-600" />
          Đang tìm người dùng...
        </div>
      {:else}
        <div
          class="flex h-52 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 text-center">
          <UserRound size={22} class="text-slate-400" />
          <p class="text-sm font-medium text-slate-700">Không tìm thấy bạn mới</p>
          <p class="text-xs text-slate-500">Thử tên, email hoặc số điện thoại khác.</p>
        </div>
      {/if}
    </div>
  </div>
</section>
